import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Mail, DollarSign, Package, Headphones, 
  Bot, Sparkles, Settings, UserPlus, ChevronLeft, ChevronRight, Megaphone,
  Phone, MessageCircle, Target, ClipboardList, ClipboardCheck
} from 'lucide-react';
import BrandLogo, { BrandMark } from '../BrandLogo';

const DashboardSidebar = ({ dealer, collapsed, onToggle, isDark, unreadEmailCount = 0, pendingCallCount = 0, newLeadCount = 0, openDealCount = 0, unreadSupportCount = 0, mobileOpen = false, onMobileToggle }) => {
  const location = useLocation();
  const isSuperAdmin = dealer?.email === 'malleg85@gmail.com';

  const totalNew = (unreadEmailCount || 0) + (pendingCallCount || 0) + (newLeadCount || 0) + (openDealCount || 0) + (unreadSupportCount || 0);

  const mainNavItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/dealer', badge: totalNew },
    { id: 'pipeline', label: 'Pipeline', icon: Users, path: '/dealer?tab=pipeline', badge: newLeadCount },
    { id: 'email', label: 'Email Center', icon: Mail, path: '/dealer?tab=email', badge: unreadEmailCount },
    { id: 'appointments', label: 'Appointments', icon: Phone, path: '/dealer?tab=appointments', badge: pendingCallCount },
    { id: 'sales', label: 'Sales', icon: DollarSign, path: '/dealer?tab=sales', badge: openDealCount },
    { id: 'trades', label: 'Trades', icon: ClipboardCheck, path: '/dealer?tab=trades' },
    // Support tab is hidden for the super-admin — the platform owner does
    // not need a customer-support inbox surfaced on their own dashboard.
    ...(isSuperAdmin ? [] : [{ id: 'support', label: 'Support', icon: Headphones, path: '/dealer?tab=support', badge: unreadSupportCount }]),
  ];

  // For every dealer: War Board + Intake Form live inline under Tools (no
  // separate Resources page). Super admin also sees the legacy Resources
  // hub with the full VIP Tools suite / Ebooks / VIP Members.
  const toolsNavItems = [
    { id: 'inventory', label: 'Inventory', icon: Package, path: '/dealer?tab=inventory' },
    { id: 'marketing', label: 'Marketing', icon: Megaphone, path: '/dealer?tab=marketing' },
    { id: 'research', label: 'Assistant', icon: Bot, path: '/dealer?tab=research' },
    { id: 'war-board', label: 'War Board', icon: Target, path: '/dealer?tab=war-board' },
    { id: 'intake-form', label: 'Intake Form', icon: ClipboardList, path: '/dealer?tab=intake-form' },
    ...(isSuperAdmin ? [{ id: 'resources', label: 'Resources', icon: Sparkles, path: '/dealer?tab=resources' }] : []),
  ];

  const adminNavItems = isSuperAdmin ? [
    { id: 'signups', label: 'Advisor Signups', icon: UserPlus, path: '/dealer?tab=signups' },
    { id: 'admin', label: 'Admin Panel', icon: Settings, path: '/dealer?tab=admin' },
  ] : [];

  const isActive = (item) => {
    if (item.path.includes('?tab=')) {
      const tab = new URLSearchParams(item.path.split('?')[1]).get('tab');
      const currentTab = new URLSearchParams(location.search).get('tab');
      return currentTab === tab;
    }
    return location.pathname === item.path && !location.search;
  };

  // Handle nav item click - close mobile menu after selection
  const handleNavClick = () => {
    if (mobileOpen && onMobileToggle) {
      onMobileToggle();
    }
  };

  const NavItem = ({ item }) => {
    const active = isActive(item);
    const Icon = item.icon;
    
    const content = (
      <div
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group cursor-pointer relative
          ${active 
            ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25' 
            : isDark 
              ? 'text-slate-400 hover:text-white hover:bg-slate-800/80' 
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        data-testid={`nav-${item.id}`}
      >
        <Icon size={20} className={`shrink-0 ${active ? 'text-white' : ''}`} />
        {!collapsed && (
          <>
            <span className={`font-medium text-sm ${collapsed ? 'opacity-0' : 'opacity-100'} transition-opacity`}>
              {item.label}
            </span>
            {item.badge > 0 && (
              <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${
                active ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
              }`}>
                {item.badge}
              </span>
            )}
          </>
        )}
        {collapsed && item.badge > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        )}
      </div>
    );

    if (item.external) {
      return <Link to={item.path} onClick={handleNavClick}>{content}</Link>;
    }
    
    return (
      <Link to={item.path} onClick={handleNavClick}>
        {content}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onMobileToggle}
          data-testid="mobile-overlay"
        />
      )}
      
      <aside 
        className={`fixed left-0 top-0 h-screen z-40 transition-all duration-300 ease-out
          ${collapsed ? 'w-20' : 'w-64'}
          ${isDark ? 'bg-[#05070d] border-r border-white/10' : 'bg-white border-r border-slate-200 shadow-sm'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="dashboard-sidebar"
      >
      {/* Logo Area */}
      <div className={`h-16 flex items-center px-4 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
        {collapsed ? (
          <BrandMark size="md" />
        ) : (
          <BrandLogo size="sm" variant="wordmark" theme={isDark ? 'dark' : 'light'} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-col h-[calc(100vh-4rem)] p-3 overflow-y-auto">
        {/* Main Nav */}
        <nav className="space-y-1">
          {mainNavItems.map(item => <NavItem key={item.id} item={item} />)}
        </nav>

        {/* Tools Section */}
        <div className="mt-6">
          {!collapsed && (
            <p className={`px-3 mb-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Tools
            </p>
          )}
          <nav className="space-y-1">
            {toolsNavItems.map(item => <NavItem key={item.id} item={item} />)}
          </nav>
        </div>

        {/* Admin Section */}
        {adminNavItems.length > 0 && (
          <div className="mt-6">
            {!collapsed && (
              <p className={`px-3 mb-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Admin
              </p>
            )}
            <nav className="space-y-1">
              {adminNavItems.map(item => <NavItem key={item.id} item={item} />)}
            </nav>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1 min-h-8" />

        {/* Collapse Toggle */}
        <div className={`sticky bottom-0 pt-2 pb-1 ${isDark ? 'bg-[#05070d]' : 'bg-white'}`}>
          <button
            onClick={onToggle}
            className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl transition-all border
              ${isDark 
                ? 'text-slate-400 hover:bg-slate-800 hover:text-white border-slate-700 hover:border-slate-600' 
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 border-slate-200 hover:border-slate-300'}`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            {!collapsed && <span className="text-sm font-medium">Collapse</span>}
          </button>
        </div>
      </div>
    </aside>
    </>
  );
};

export default DashboardSidebar;
